import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { ArrowUpRight } from "lucide-react";
import andreaImg from "@/assets/andrea-2.jpg";
import edwinImg from "@/assets/edwin-2.jpg";
import evertImg from "@/assets/evert-2.jpg";
import { SectionClouds } from "./SectionClouds";
import { useLocalizedHref } from "@/hooks/useLocalizedHref";

type MemberId = "andrea" | "edwin" | "evert";

interface Member {
  id: MemberId;
  name: string;
  photo: string;
  linkedin: string;
}

interface MemberData {
  role: string;
  bio: string;
}

const team: Member[] = [
  {
    id: "andrea",
    name: "Andrea",
    photo: andreaImg,
    linkedin: "https://www.linkedin.com/in/andreacabral/",
  },
  {
    id: "edwin",
    name: "Edwin",
    photo: edwinImg,
    linkedin: "https://www.linkedin.com/in/edwinwesterhoud/",
  },
  {
    id: "evert",
    name: "Evert",
    photo: evertImg,
    linkedin: "https://www.linkedin.com/in/evert-rotshuizen-2637a15b/",
  },
];

export function Team() {
  const { t } = useTranslation();
  const localizedHref = useLocalizedHref();
  const [isVisible, setIsVisible] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} id="team" className="frame bg-page-bg">
      <SectionClouds variant="team" />
      <div className="container-narrow section-pad relative z-10">
        <div
          className={cn(
            "transition-all duration-700",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
          )}
        >
          <span className="eyebrow">
            <span className="num">07 —</span> {t("landing.team.eyebrow")}
          </span>
          <h2 className="section-title">
            <Trans
              i18nKey="landing.team.title"
              components={{ italic: <span className="serif-italic" /> }}
            />
          </h2>
          <p className="section-lede">{t("landing.team.lede")}</p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-3 lg:gap-7">
          {team.map((member, i) => {
            const data = t(`landing.team.members.${member.id}`, {
              returnObjects: true,
            }) as MemberData;
            return (
              <div
                key={member.id}
                className={cn(
                  "lift flex flex-col rounded-[22px] border border-line bg-surface p-7",
                  "transition-all duration-700",
                  isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
                )}
                style={{ transitionDelay: isVisible ? `${i * 80}ms` : "0ms" }}
              >
                <div className="aspect-[1.1] overflow-hidden rounded-[16px] bg-surface-2">
                  <img
                    src={member.photo}
                    alt={`${member.name}, ${data.role}`}
                    loading="lazy"
                    className="h-full w-full object-cover object-top"
                  />
                </div>

                <div className="mt-5 flex flex-1 flex-col">
                  <div className="text-[20px] leading-tight font-medium text-ink">
                    {member.name}
                  </div>
                  <div className="mono mt-1 text-[11px] tracking-[0.14em] text-accent uppercase">
                    {data.role}
                  </div>
                  <p className="mt-3 text-[13.5px] leading-[1.6] text-ink-2">{data.bio}</p>
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-auto inline-flex items-center gap-1 self-start pt-4 text-[13px] font-medium text-ink transition-colors hover:text-accent"
                  >
                    {t("landing.team.linkedin")}{" "}
                    <ArrowUpRight className="h-3 w-3" strokeWidth={2} />
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <Link
            to={localizedHref("/about")}
            className="text-[15px] font-medium text-accent transition-colors hover:text-ink"
          >
            {t("landing.team.aboutLink")}
          </Link>
        </div>
      </div>
    </section>
  );
}
